import { createRenderer } from 'fela';
import type { GatsbyBrowser } from 'gatsby';
import { RendererProvider, ThemeProvider } from 'react-fela';

import { CurrencyProvider, HTMLLanguageProvider } from '~localization';
import { captureInitialUrlSearch, ConsentProvider } from '~modules/cookie-consent';

import config from './fela.config';
import theme from './src/styles/theme';

import './src/styles/index.css';

const renderer = createRenderer(config);

const Gatsby: GatsbyBrowser = {
    async onClientEntry() {
        captureInitialUrlSearch();

        /* Support intl in older browsers */
        if (!window.Intl) {
            await import(`intl`);
        }
    },
    async onInitialClientRender() {
        await import('url-search-params-polyfill');
    },
    wrapPageElement({ element, props }) {
        const pageContext = props.pageContext as {
            language: string;
        };

        return (
            <HTMLLanguageProvider language={pageContext.language}>
                <CurrencyProvider locale={pageContext.language}>{element}</CurrencyProvider>
            </HTMLLanguageProvider>
        );
    },
    wrapRootElement({ element }) {
        return (
            <ThemeProvider theme={theme}>
                <RendererProvider rehydrate renderer={renderer}>
                    <ConsentProvider>{element}</ConsentProvider>
                </RendererProvider>
            </ThemeProvider>
        );
    },
    shouldUpdateScroll({ routerProps: { location }, getSavedScrollPosition }) {
        if (Boolean(location.hash)) {
            return location.hash.replace('#', '');
        }

        // A link to a bare `#` leaves an empty hash, but the browser treats it as "go to the top".
        // Its history entry has no state, so the saved position would be the initial page's.
        const coords = window.location.href.endsWith('#') ? null : getSavedScrollPosition(location);

        // Defer scroll to to the next tick to avoid layout shift blocking scroll
        setTimeout(() => {
            window.scrollTo(...(coords || [0, 0]));
        }, 0);

        return false;
    },
};

const { onClientEntry, onInitialClientRender, wrapPageElement, wrapRootElement, shouldUpdateScroll } = Gatsby;

export { onClientEntry, onInitialClientRender, shouldUpdateScroll, wrapPageElement, wrapRootElement };
